import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Ban, FileText, Plus, Printer, Search, ShoppingCart, Trash2 } from 'lucide-react';
import * as api from '@/api/cotizaciones.api';
import type { Cotizacion, CotizacionEnLista, EstadoDeCotizacion } from '@/api/cotizaciones.api';
import * as tienda from '@/api/tienda.api';
import type { Producto } from '@/api/tienda.api';
import { AVISO_DE_APROBACION, quedoEnAprobaciones } from '@/api/aprobaciones.api';
import { useAbrirDetalle } from '@/components/datos/contexto-detalle';
import { ModuleHeader, Kpi } from '@/components/datos/ModuleHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import { dinero, numero, fecha, fechaDia } from '@/lib/formato-tienda';

/**
 * COTIZACIONES — lo que FacturApp trae y aquí faltaba.
 *
 * Una cotización es un precio por escrito, con su número propio («C-000001»). No es una venta:
 * no descuenta ni aparta existencia. Cuando el cliente dice que sí, se convierte en una venta
 * con esos mismos precios, y esa venta se confirma y se factura como cualquier otra.
 */

const ESTADO: Record<EstadoDeCotizacion, { texto: string; tono: 'info' | 'warning' | 'success' | 'default' }> = {
  ABIERTA: { texto: 'Abierta', tono: 'info' },
  VENCIDA: { texto: 'Vencida', tono: 'warning' },
  CONVERTIDA: { texto: 'Convertida en venta', tono: 'success' },
  ANULADA: { texto: 'Anulada', tono: 'default' },
};
const sigueViva = (e: EstadoDeCotizacion) => e === 'ABIERTA' || e === 'VENCIDA';
const mensajeDe = (e: unknown, defecto: string) => (e instanceof Error ? e.message : defecto);
const CLAVE = ['cotizaciones'] as const;

/** Hoy más `dias`, como lo pide un `<input type="date">`. */
function diaEn(dias: number): string {
  const d = new Date(Date.now() + dias * 86_400_000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function Cotizaciones({ onVolver }: { onVolver: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const abrir = useAbrirDetalle();
  const [nueva, setNueva] = useState(false);
  const [viendo, setViendo] = useState<string | null>(null);
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: CLAVE, queryFn: api.listarCotizaciones });
  const lista = data ?? [];

  const refrescar = () => {
    void qc.invalidateQueries({ queryKey: CLAVE });
    void qc.invalidateQueries({ queryKey: ['cotizacion'] });
  };
  const abrirVenta = (v: { id: string; numero: string }) => abrir?.({ tipo: 'venta', id: v.id, etiqueta: v.numero });

  const convertir = useMutation({
    mutationFn: (id: string) => api.convertirEnVenta(id),
    onSuccess: ({ cotizacion, venta }) => {
      toast.success(`${cotizacion.numero} ya es la venta ${venta.numero}, en borrador. Confírmala o factúrala para que descuente.`);
      refrescar();
      void qc.invalidateQueries({ queryKey: ['ventas'] });
      setViendo(null);
      abrirVenta(venta);
    },
    onError: (e) => {
      if (quedoEnAprobaciones(e)) {
        toast.info(AVISO_DE_APROBACION, e.message);
        setViendo(null);
      } else toast.error(mensajeDe(e, 'No se pudo convertir en venta'));
      refrescar();
    },
  });
  const anular = useMutation({
    mutationFn: (id: string) => api.anularCotizacion(id),
    onSuccess: (c) => {
      toast.success(`Cotización ${c.numero} anulada.`);
      refrescar();
    },
    onError: (e) => {
      toast.error(mensajeDe(e, 'No se pudo anular'));
      refrescar();
    },
  });
  const ocupado = convertir.isPending || anular.isPending;

  const vivas = lista.filter((c) => sigueViva(c.estadoALaVista));
  const vencidas = lista.filter((c) => c.estadoALaVista === 'VENCIDA');
  const convertidas = lista.filter((c) => c.estadoALaVista === 'CONVERTIDA');

  return (
    <div data-cotizaciones className="space-y-5">
      <ModuleHeader
        titulo="Cotizaciones"
        subtitulo="Un precio por escrito, con su número. No descuenta existencia; cuando el cliente dice que sí, se convierte en venta."
        acciones={
          <>
            <Button variant="outline" onClick={onVolver}>
              <ArrowLeft className="h-4 w-4" />
              Volver a las facturas
            </Button>
            <Button onClick={() => setNueva(true)}>
              <Plus className="h-4 w-4" />
              Nueva cotización
            </Button>
          </>
        }
        kpis={
          <>
            <Kpi titulo="Esperando respuesta" valor={dinero(vivas.reduce((a, c) => a + c.total, 0))} pie={`${vivas.length} cotizaci${vivas.length === 1 ? 'ón' : 'ones'}`} />
            <Kpi titulo="Vencidas" valor={numero(vencidas.length)} pie="se les pasó el día" tono={vencidas.length > 0 ? 'malo' : 'normal'} bajarEsBueno />
            <Kpi titulo="Convertidas en venta" valor={numero(convertidas.length)} pie={dinero(convertidas.reduce((a, c) => a + c.total, 0))} />
          </>
        }
      />

      {isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : isError ? (
        <div className="rounded-2xl border border-surface-border bg-surface p-6 text-center">
          <p className="text-sm text-slate-600 dark:text-slate-300">No se pudieron cargar las cotizaciones.</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => void refetch()}>
            Reintentar
          </Button>
        </div>
      ) : lista.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-surface-border p-8 text-center">
          <FileText className="mx-auto h-8 w-8 text-slate-400" />
          <p className="mt-2 font-medium text-slate-800 dark:text-slate-100">Todavía no hay cotizaciones</p>
          <p className="mt-1 text-sm text-slate-500">Hazle una al cliente que pide precio por escrito. No toca el inventario.</p>
          <Button className="mt-3" onClick={() => setNueva(true)}>
            Nueva cotización
          </Button>
        </div>
      ) : (
        <ul className="space-y-3">
          {lista.map((c) => (
            <FilaDeCotizacion
              key={c.id}
              c={c}
              ocupado={ocupado}
              onVer={() => setViendo(c.id)}
              onConvertir={() => convertir.mutate(c.id)}
              onAnular={() => anular.mutate(c.id)}
              onAbrirVenta={abrirVenta}
            />
          ))}
        </ul>
      )}

      {nueva && (
        <NuevaCotizacion
          onCerrar={() => setNueva(false)}
          onCreada={(c) => {
            setNueva(false);
            refrescar();
            setViendo(c.id);
          }}
        />
      )}
      {viendo && (
        <VerCotizacion
          id={viendo}
          ocupado={ocupado}
          onCerrar={() => setViendo(null)}
          onConvertir={() => convertir.mutate(viendo)}
          onAnular={() => anular.mutate(viendo)}
          onAbrirVenta={(v) => {
            setViendo(null);
            abrirVenta(v);
          }}
        />
      )}
    </div>
  );
}

function FilaDeCotizacion({
  c,
  ocupado,
  onVer,
  onConvertir,
  onAnular,
  onAbrirVenta,
}: {
  c: CotizacionEnLista;
  ocupado: boolean;
  onVer: () => void;
  onConvertir: () => void;
  onAnular: () => void;
  onAbrirVenta: (v: { id: string; numero: string }) => void;
}) {
  return (
    <li data-cotizacion={c.numero} className="rounded-2xl border border-surface-border bg-surface p-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="font-mono font-semibold text-slate-900 dark:text-slate-100">{c.numero}</span>
        <Badge variant={ESTADO[c.estadoALaVista].tono}>{ESTADO[c.estadoALaVista].texto}</Badge>
        <span className="text-sm text-slate-700 dark:text-slate-200">{c.clienteNombre}</span>
        <span className="ml-auto text-base font-semibold tabular-nums text-slate-900 dark:text-slate-100">{dinero(c.total)}</span>
      </div>
      <p className="mt-1 text-xs text-slate-500">
        {fecha(c.createdAt)} · {numero(c.piezas)} pieza{c.piezas === 1 ? '' : 's'}
        {c.validaHasta && sigueViva(c.estadoALaVista) ? ` · vale hasta el ${fechaDia(c.validaHasta)}` : ''}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={onVer}>
          <FileText className="h-3.5 w-3.5" /> Ver / imprimir
        </Button>
        {sigueViva(c.estadoALaVista) && (
          <>
            <Button size="sm" disabled={ocupado} onClick={onConvertir}>
              <ShoppingCart className="h-3.5 w-3.5" /> Convertir en venta
            </Button>
            <Button variant="ghost" size="sm" disabled={ocupado} onClick={onAnular}>
              <Ban className="h-3.5 w-3.5" /> Anular
            </Button>
          </>
        )}
        {c.venta && (
          <Button variant="outline" size="sm" onClick={() => onAbrirVenta(c.venta!)}>
            <ShoppingCart className="h-3.5 w-3.5" /> Abrir la venta {c.venta.numero}
          </Button>
        )}
      </div>
    </li>
  );
}

function VerCotizacion({
  id,
  ocupado,
  onCerrar,
  onConvertir,
  onAnular,
  onAbrirVenta,
}: {
  id: string;
  ocupado: boolean;
  onCerrar: () => void;
  onConvertir: () => void;
  onAnular: () => void;
  onAbrirVenta: (v: { id: string; numero: string }) => void;
}) {
  const { data: c, isLoading } = useQuery({ queryKey: ['cotizacion', id], queryFn: () => api.leerCotizacion(id) });
  return (
    <Modal open onClose={onCerrar} size="xl" title={c ? `Cotización ${c.numero}` : 'Cotización'}>
      {isLoading || !c ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={ESTADO[c.estadoALaVista].tono}>{ESTADO[c.estadoALaVista].texto}</Badge>
            <span className="ml-auto flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => window.print()}>
                <Printer className="h-3.5 w-3.5" /> Imprimir / PDF
              </Button>
              {sigueViva(c.estadoALaVista) && (
                <>
                  <Button size="sm" disabled={ocupado} onClick={onConvertir}>
                    <ShoppingCart className="h-3.5 w-3.5" /> Convertir en venta
                  </Button>
                  <Button size="sm" variant="ghost" disabled={ocupado} onClick={onAnular}>
                    <Ban className="h-3.5 w-3.5" /> Anular
                  </Button>
                </>
              )}
              {c.venta && (
                <Button size="sm" variant="outline" onClick={() => onAbrirVenta(c.venta!)}>
                  <ShoppingCart className="h-3.5 w-3.5" /> Abrir la venta {c.venta.numero}
                </Button>
              )}
            </span>
          </div>
          <PapelDeCotizacion c={c} />
        </div>
      )}
    </Modal>
  );
}

/** Lo que se imprime. El `id` es el de la factura: el CSS de impresión ya sabe aislarlo. */
function PapelDeCotizacion({ c }: { c: Cotizacion }) {
  return (
    <div id="factura-para-imprimir" className="rounded-lg border border-surface-border bg-surface p-5 sm:p-7">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p
            className="text-xl font-semibold text-slate-900 dark:text-slate-100"
            style={{ fontFamily: 'var(--f-titulos, inherit)', letterSpacing: 'var(--f-titulos-espacio, normal)' }}
          >
            {c.emisor.nombre || 'Tu tienda'}
          </p>
          {c.emisor.rnc && <p className="text-xs text-slate-500">RNC {c.emisor.rnc}</p>}
          {c.emisor.direccion && <p className="text-xs text-slate-500">{c.emisor.direccion}</p>}
          {c.emisor.telefono && <p className="text-xs text-slate-500">{c.emisor.telefono}</p>}
        </div>
        <div className="text-right">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Cotización</p>
          <p className="font-mono text-base text-slate-900 dark:text-slate-100">{c.numero}</p>
          <p className="text-xs text-slate-500">{fecha(c.createdAt, true)}</p>
          {c.validaHasta && <p className="mt-1 text-xs text-slate-500">Vale hasta el {fechaDia(c.validaHasta)}</p>}
          {c.estadoALaVista === 'ANULADA' && <p className="mt-1 text-sm font-semibold uppercase text-red-600">Anulada</p>}
        </div>
      </div>

      <div className="mt-4 border-t border-surface-border pt-3 text-sm">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Para</p>
        <p className="text-slate-900 dark:text-slate-100">{c.clienteNombre}</p>
        {c.clienteTelefono && <p className="text-xs text-slate-500">{c.clienteTelefono}</p>}
      </div>

      <table className="mt-4 w-full text-sm">
        <thead>
          <tr className="border-b border-surface-border text-left text-xs uppercase tracking-wide text-slate-500">
            <th className="py-1.5 pr-2 font-medium">Cant.</th>
            <th className="py-1.5 pr-2 font-medium">Descripción</th>
            <th className="py-1.5 pr-2 text-right font-medium">Precio</th>
            <th className="py-1.5 text-right font-medium">Importe</th>
          </tr>
        </thead>
        <tbody>
          {c.lineas.map((l) => (
            <tr key={l.productVariantId} className="border-b border-surface-border/60 align-top">
              <td className="py-1.5 pr-2 tabular-nums">{l.cantidad}</td>
              <td className="py-1.5 pr-2">
                {l.descripcion} <span className="text-slate-500">· {l.talla}</span>
                {l.sku && <span className="block text-xs text-slate-500">{l.sku}</span>}
                {l.descuento > 0 && <span className="block text-xs text-slate-500">Rebaja de {dinero(l.descuento)}</span>}
              </td>
              <td className="py-1.5 pr-2 text-right tabular-nums">{dinero(l.precio)}</td>
              <td className="py-1.5 text-right tabular-nums">{dinero(l.importe)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <dl className="ml-auto mt-3 w-full max-w-xs space-y-1 text-sm">
        <div className="flex justify-between">
          <dt className="text-slate-500">Subtotal</dt>
          <dd className="tabular-nums">{dinero(c.subtotal)}</dd>
        </div>
        {c.descuento > 0 && (
          <div className="flex justify-between">
            <dt className="text-slate-500">Descuento</dt>
            <dd className="tabular-nums">− {dinero(c.descuento)}</dd>
          </div>
        )}
        {c.envio > 0 && (
          <div className="flex justify-between">
            <dt className="text-slate-500">Envío</dt>
            <dd className="tabular-nums">{dinero(c.envio)}</dd>
          </div>
        )}
        <div className="flex justify-between border-t border-surface-border pt-1 text-base font-semibold">
          <dt>Total</dt>
          <dd className="tabular-nums">{dinero(c.total)}</dd>
        </div>
      </dl>

      {c.notas && <p className="mt-4 whitespace-pre-wrap text-sm text-slate-600 dark:text-slate-300">{c.notas}</p>}
      <p className="mt-4 border-t border-surface-border pt-3 text-xs text-slate-500">
        Esto es una cotización: no es una factura ni un comprobante fiscal, y no aparta la mercancía. Los precios valen
        {c.validaHasta ? ` hasta el ${fechaDia(c.validaHasta)}` : ' mientras haya existencia'}.
      </p>
    </div>
  );
}

interface Renglon {
  productVariantId: string;
  etiqueta: string;
  talla: string;
  cantidad: string;
  precio: string;
  descuento: string;
}

function NuevaCotizacion({ onCerrar, onCreada }: { onCerrar: () => void; onCreada: (c: Cotizacion) => void }) {
  const toast = useToast();
  const [renglones, setRenglones] = useState<Renglon[]>([]);
  const [buscar, setBuscar] = useState('');
  const [cliente, setCliente] = useState<{ id: string; nombre: string } | null>(null);
  const [nombre, setNombre] = useState('');
  const [telefono, setTelefono] = useState('');
  const [envio, setEnvio] = useState('');
  const [descuento, setDescuento] = useState('');
  const [validaHasta, setValidaHasta] = useState(() => diaEn(15));
  const [notas, setNotas] = useState('');

  const productos = useQuery({
    queryKey: ['productos', 'cotizar', buscar],
    queryFn: () => tienda.listarProductos({ buscar, limit: 12 }),
    enabled: buscar.trim().length > 0,
  });
  const clientes = useQuery({
    queryKey: ['clientes', 'cotizar', nombre],
    queryFn: () => tienda.listarClientes({ buscar: nombre, limit: 5 }),
    enabled: !cliente && nombre.trim().length >= 2,
  });

  const agregar = (p: Producto, varianteId: string) => {
    const v = p.variants.find((x) => x.id === varianteId);
    if (!v || renglones.some((r) => r.productVariantId === varianteId)) return;
    setRenglones((prev) => [
      ...prev,
      { productVariantId: v.id, etiqueta: p.name, talla: v.label, cantidad: '1', precio: String(v.price ?? p.price), descuento: '' },
    ]);
  };
  const cambiar = (id: string, campo: 'cantidad' | 'precio' | 'descuento', valor: string) =>
    setRenglones((prev) => prev.map((r) => (r.productVariantId === id ? { ...r, [campo]: valor } : r)));

  const n = (s: string) => Math.floor(Number(s) || 0);
  const lineas = renglones.map((r) => ({ productVariantId: r.productVariantId, cantidad: n(r.cantidad), unitPrice: n(r.precio), descuento: n(r.descuento) }));
  const subtotal = lineas.reduce((a, l) => a + l.unitPrice * l.cantidad - l.descuento, 0);
  const total = Math.max(0, subtotal - n(descuento)) + n(envio);
  const aNombreDe = cliente?.nombre ?? nombre.trim();
  const valida =
    lineas.length > 0 &&
    lineas.every((l) => l.cantidad >= 1 && l.unitPrice >= 0 && l.descuento >= 0 && l.descuento <= l.unitPrice * l.cantidad) &&
    Boolean(aNombreDe) &&
    n(descuento) <= subtotal;

  const crear = useMutation({
    mutationFn: () =>
      api.crearCotizacion({
        lineas: lineas.map((l) => ({ ...l, descuento: l.descuento || undefined })),
        clienteId: cliente?.id ?? null,
        clienteNombre: cliente ? null : nombre.trim(),
        clienteTelefono: cliente ? null : telefono.trim() || null,
        envio: n(envio) || undefined,
        descuento: n(descuento) || undefined,
        validaHasta: validaHasta || null,
        notas: notas.trim() || null,
      }),
    onSuccess: (c) => {
      toast.success(`Cotización ${c.numero} lista. No tocó el inventario.`);
      onCreada(c);
    },
    onError: (e) => {
      if (quedoEnAprobaciones(e)) {
        toast.info(AVISO_DE_APROBACION, e.message);
        return onCerrar();
      }
      toast.error(mensajeDe(e, 'No se pudo crear la cotización'));
    },
  });

  return (
    <Modal open onClose={onCerrar} size="xl" title="Nueva cotización" description="Un precio por escrito. No descuenta ni aparta existencia.">
      <div className="space-y-5">
        {/* ── Para quién ── */}
        <fieldset>
          <legend className="text-sm font-medium text-slate-800 dark:text-slate-100">¿Para quién es?</legend>
          {cliente ? (
            <div className="mt-2 flex min-h-[40px] items-center justify-between gap-2 rounded-lg border border-surface-border px-3 text-sm">
              <span>{cliente.nombre}</span>
              <button type="button" className="text-xs underline" onClick={() => setCliente(null)}>
                cambiar
              </button>
            </div>
          ) : (
            <>
              <div className="mt-2 grid gap-2 sm:grid-cols-[2fr_1fr]">
                <Input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Nombre de la persona o la empresa" aria-label="A nombre de" />
                <Input value={telefono} onChange={(e) => setTelefono(e.target.value)} placeholder="Teléfono (opcional)" aria-label="Teléfono" />
              </div>
              {(clientes.data ?? []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  <span className="text-xs text-slate-500">Ya es cliente:</span>
                  {(clientes.data ?? []).map((x) => (
                    <button
                      key={x.id}
                      type="button"
                      className="min-h-[32px] rounded-full border border-surface-border px-3 text-xs text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
                      onClick={() => setCliente({ id: x.id, nombre: x.nombre })}
                    >
                      {x.nombre}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </fieldset>

        {/* ── Qué se cotiza ── */}
        <fieldset>
          <legend className="text-sm font-medium text-slate-800 dark:text-slate-100">¿Qué se cotiza?</legend>
          <div className="relative mt-2">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9" value={buscar} onChange={(e) => setBuscar(e.target.value)} placeholder="Busca la pieza por nombre o código…" aria-label="Buscar pieza" />
          </div>
          {buscar.trim() && (
            <div className="mt-2 max-h-48 space-y-2 overflow-y-auto rounded-xl border border-surface-border p-2">
              {productos.isLoading ? (
                <p className="p-2 text-sm text-slate-500">Buscando…</p>
              ) : (productos.data ?? []).length === 0 ? (
                <p className="p-2 text-sm text-slate-500">Ninguna pieza con ese nombre.</p>
              ) : (
                (productos.data ?? []).map((p) => (
                  <div key={p.id} className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="min-w-[8rem] flex-1 text-slate-800 dark:text-slate-100">
                      {p.name} <span className="text-slate-500">· {dinero(p.price)}</span>
                    </span>
                    {p.variants.map((v) => {
                      const puesta = renglones.some((r) => r.productVariantId === v.id);
                      return (
                        <button
                          key={v.id}
                          type="button"
                          disabled={puesta}
                          onClick={() => agregar(p, v.id)}
                          className="min-h-[32px] rounded-full border border-surface-border px-3 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:text-slate-200 dark:hover:bg-slate-800"
                          title={puesta ? 'Ya está en la cotización' : `Añadir la talla ${v.label}`}
                        >
                          {v.label} <span className="text-slate-400">({numero(v.stock)})</span>
                        </button>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
          )}

          {renglones.length === 0 ? (
            <p className="mt-3 text-sm text-slate-500">Todavía no hay piezas. Búscalas arriba y toca la talla.</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {renglones.map((r) => (
                // En el teléfono el nombre ocupa su renglón y debajo van cantidad, precio y rebaja.
                <li key={r.productVariantId} className="grid grid-cols-[1fr_1.4fr_1.4fr_auto] items-center gap-2 rounded-xl border border-surface-border p-2 sm:grid-cols-[1fr_5rem_7rem_7rem_auto]">
                  <span className="col-span-4 min-w-0 text-sm text-slate-800 sm:col-span-1 dark:text-slate-100">
                    {r.etiqueta} <span className="text-slate-500">· {r.talla}</span>
                  </span>
                  <Input type="number" min={1} className="h-9 text-right" placeholder="cant." aria-label={`Cantidad de ${r.etiqueta} ${r.talla}`} value={r.cantidad} onChange={(e) => cambiar(r.productVariantId, 'cantidad', e.target.value)} />
                  <Input type="number" min={0} className="h-9 text-right" placeholder="precio" aria-label={`Precio de ${r.etiqueta} ${r.talla}`} value={r.precio} onChange={(e) => cambiar(r.productVariantId, 'precio', e.target.value)} />
                  <Input type="number" min={0} className="h-9 text-right" placeholder="rebaja" aria-label={`Rebaja de ${r.etiqueta} ${r.talla}`} value={r.descuento} onChange={(e) => cambiar(r.productVariantId, 'descuento', e.target.value)} />
                  <Button variant="ghost" size="icon" aria-label={`Quitar ${r.etiqueta} ${r.talla}`} title="Quitar" onClick={() => setRenglones((prev) => prev.filter((x) => x.productVariantId !== r.productVariantId))}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </fieldset>

        {/* ── Cierre ── */}
        <div className="grid gap-2 sm:grid-cols-3">
          <label className="block text-xs text-slate-500">
            Envío
            <Input type="number" min={0} className="mt-1" value={envio} onChange={(e) => setEnvio(e.target.value)} placeholder="0" />
          </label>
          <label className="block text-xs text-slate-500">
            Descuento
            <Input type="number" min={0} className="mt-1" value={descuento} onChange={(e) => setDescuento(e.target.value)} placeholder="0" />
          </label>
          <label className="block text-xs text-slate-500">
            Vale hasta
            <Input type="date" className="mt-1" min={diaEn(0)} value={validaHasta} onChange={(e) => setValidaHasta(e.target.value)} />
          </label>
        </div>
        <label className="block text-xs text-slate-500">
          Nota para el cliente (opcional)
          <Input className="mt-1" value={notas} onChange={(e) => setNotas(e.target.value)} placeholder="Tiempo de entrega, forma de pago…" />
        </label>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-surface-border pt-4">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Total <strong className="text-lg tabular-nums text-slate-900 dark:text-slate-100">{dinero(total)}</strong>
          </p>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onCerrar}>
              Cancelar
            </Button>
            <Button onClick={() => crear.mutate()} disabled={!valida || crear.isPending} loading={crear.isPending}>
              Crear cotización
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
